<script setup lang="ts">
import { ImportingService } from "../utils/processors/mutua";
import StreetFromCard from "@/components/Services/StreetFromCard.vue";
import { ref, watch, nextTick } from "vue";

const props = defineProps<{
  services: ImportingService[];
}>();

const emit = defineEmits(["validationChange", "create"]);

const formRefs = ref<any[]>([]);

const validationRules = {
  "customer.phone": [{ required: true, message: "El teléfono es obligatorio", trigger: "blur" }],
  "passenger.name": [
    { required: true, message: "El nombre del pasajero es obligatorio", trigger: "blur" },
  ],
  "origin.street": [
    { required: true, message: "La calle de origen es obligatoria", trigger: "change" },
  ],
  "origin.notes": [
    { required: true, message: "Las notas de origen son obligatorias", trigger: "blur" },
  ],
  "destination.street": [
    { required: true, message: "La calle de destino es obligatoria", trigger: "change" },
  ],
  "destination.notes": [
    { required: true, message: "Las notas de destino son obligatorias", trigger: "blur" },
  ],
  date: [{ required: true, message: "La fecha es obligatoria", trigger: "change" }],
  hour: [{ required: true, message: "La hora es obligatoria", trigger: "blur" }],
  advance: [
    { required: true, message: "La antelación es obligatoria", trigger: "blur" },
    {
      type: "number",
      min: 0,
      message: "La antelación debe ser mayor o igual a 0",
      trigger: "blur",
    },
  ],
};

const isFormValid = ref(true);

const validateForm = async () => {
  try {
    const promises = formRefs.value.map((formRef) => {
      if (formRef) {
        return formRef.validate();
      }
      return Promise.resolve();
    });

    await Promise.all(promises);
    isFormValid.value = true;
    emit("validationChange", true);
    return true;
  } catch (error) {
    console.warn(error);
    isFormValid.value = false;
    emit("validationChange", false);
    return false;
  }
};

watch(
  () => props.services,
  async () => {
    console.log("validate form");
    await nextTick();
    await validateForm();
  },
  { deep: true, immediate: true }
);

watch(
  () => props.services.length,
  async () => {
    await nextTick();
  }
);

const getStreetValidations = (service: ImportingService, field: "origin" | "destination") => {
  const hasStreet = !!service[field].street;
  const hasPortal = service[field].portal !== undefined;
  return {
    street: {
      $dirty: true,
      required: hasStreet,
      $error: !hasStreet,
    },
    portal: {
      $dirty: true,
      required: hasPortal,
      $error: !hasPortal,
    },
  };
};

defineExpose({
  validateForm,
  isFormValid,
});
</script>

<template>
  <el-card>
    <div class="service-info">
      <div class="service-name">
        <h3>Servicio {{ services[0].id }}: {{ isFormValid }}</h3>
        <div class="services">
          <div v-for="(service, index) in services" :key="service.key" class="service">
            <div class="service">
              <div v-if="services.length > 1" class="service-header">
                <span v-if="index === 0">Ida</span>
                <span v-else>Vuelta</span>
              </div>
              <el-card shadow="never">
                <el-form
                  :ref="(el) => el && (formRefs[index] = el)"
                  :model="service"
                  :rules="validationRules"
                  size="mini"
                  label-position="top"
                >
                  <div class="form-row">
                    <el-form-item label="Teléfono" style="width: 200px" prop="customer.phone">
                      <el-input v-model="service.customer.phone" />
                    </el-form-item>
                    <el-form-item label="Pasajero" style="width: 100%" prop="passenger.name">
                      <el-input v-model="service.passenger.name" />
                    </el-form-item>
                  </div>
                  <div class="form-row">
                    <street-from-card
                      ref="streetFromCard"
                      v-model="service.origin"
                      :provided-address="service.origin.address"
                      :is-just-street="true"
                      :validations="getStreetValidations(service, 'origin')"
                      :workgroupId="service.wgId"
                      is-edit
                    />
                  </div>
                  <div class="form-row">
                    <el-form-item label="Notas de origen" style="width: 100%" prop="origin.notes">
                      <el-input v-model="service.origin.notes" />
                    </el-form-item>
                  </div>
                  <div class="form-row">
                    <street-from-card
                      ref="streetFromCard"
                      v-model="service.destination"
                      :provided-address="service.destination.address"
                      :is-just-street="true"
                      :validations="getStreetValidations(service, 'destination')"
                      :workgroupId="service.wgId"
                      is-edit
                    />
                  </div>
                  <div class="form-row">
                    <el-form-item
                      label="Notas de destino"
                      style="width: 100%"
                      prop="destination.notes"
                    >
                      <el-input v-model="service.destination.notes" />
                    </el-form-item>
                  </div>
                  <div class="form-row">
                    <el-form-item label="Fecha y hora" prop="date">
                      <el-date-picker v-model="service.date" format="DD/MM/yyyy" type="date" />
                    </el-form-item>
                    <el-form-item label="Hora" prop="hour">
                      <el-input v-model="service.hour" format="HH:mm" type="time" />
                    </el-form-item>
                    <el-form-item label="Antelación" prop="advance">
                      <el-input-number v-model="service.advance" :min="0" :max="100" />
                    </el-form-item>
                  </div>
                </el-form>
              </el-card>
            </div>
          </div>
        </div>
      </div>
    </div>
  </el-card>
</template>

<style scoped>
h3 {
  margin: 0 0 10px 0;
  font-size: 12px;
  font-weight: 600;
}
.form-row {
  display: flex;
  gap: 10px;
  width: 100%;
  flex: 1;
}
.services {
  display: flex;
  gap: 15px;
  width: 100%;
  flex: 1;
}
.service {
  width: 100%;
}
.service-header {
  text-transform: uppercase;
  display: flex;
  font-weight: bold;
  font-size: 12px;
  margin-bottom: 5px;
}
</style>
